import { Gift } from "lucide-react";
import type { ReactNode } from "react";
import { trpc } from "~/api/trpc";
import { type CartItem, getCartItemTotalPrice, getEntirePurchaseDiscountAmount, getPerItemDiscountAmounts, type MatchedDiscountCode } from "~/stores/booking-flow-store";
import { Language, useLanguageStore } from "~/stores/language-store";

type ServicesListProps = {
    cartItems: CartItem[];
    matchedDiscountCode: MatchedDiscountCode | null;
    discountCode: string;
    headerAction?: ReactNode;
    children?: ReactNode;
};

const ServicesList = ({ cartItems, matchedDiscountCode, discountCode, headerAction, children }: ServicesListProps) => {
    const { language } = useLanguageStore();

    // CMS Config
    const { data: cmsConfigData } = trpc.publicBookingFunnel.getBookingReviewConfig.useQuery(undefined, {
        staleTime: 60000,
    });
    const cmsConfig = cmsConfigData?.config;

    const subtotalPrice = cartItems.reduce((current, item) => current + getCartItemTotalPrice(item), 0);
    const displaySubtotalPrice = cartItems.reduce((current, item) => current + (item.isBundleRedemption ? 0 : getCartItemTotalPrice(item)), 0);
    const perItemDiscount = getPerItemDiscountAmounts(cartItems, matchedDiscountCode).reduce((current, amount) => current + amount, 0);
    const entirePurchaseDiscount = getEntirePurchaseDiscountAmount(subtotalPrice, matchedDiscountCode);
    const totalDiscount = perItemDiscount + entirePurchaseDiscount;
    const displayTotalAfterDiscount = displaySubtotalPrice - totalDiscount;

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price);

    return (
        <div className='flex flex-col gap-3'>
            <div className='flex items-baseline justify-between gap-4'>
                <span className='text-lg font-normal text-burgundy'>
                    {language === Language.GERMAN ? (cmsConfig?.treatmentsTitleDe ?? 'Behandlungen') : (cmsConfig?.treatmentsTitleEn ?? 'Treatments')}
                </span>
                {headerAction}
            </div>

            <div className='flex flex-col gap-3'>
                {cartItems.map((item, index) => {
                    const isRedemption = !!item.isBundleRedemption;
                    const isBundle = item.bookableItemType === 'BUNDLE' && item.bundleId && !isRedemption;
                    const bundleTotalPrice = isRedemption && item.bundleUnitPriceGross
                        ? item.bundleUnitPriceGross
                        : isBundle && item.bundleUnitPriceGross && item.bundleUnitQuantity
                            ? item.bundleUnitPriceGross * item.bundleUnitQuantity
                            : item.price;
                    const displayName = isBundle && item.bundleName
                        ? item.bundleName
                        : item.selectedVariantName;

                    return (
                        <div key={index} className='flex flex-col gap-1'>
                            {/* Main item */}
                            <div className='flex flex-row items-center justify-between'>
                                <div className='flex items-center gap-2'>
                                    {(isBundle || isRedemption) && (
                                        <Gift className='w-4 h-4 text-burgundy shrink-0' />
                                    )}
                                    <span className='text-sm text-burgundy font-light'>
                                        {displayName}
                                    </span>
                                </div>
                                <span className='text-sm text-burgundy font-light'>
                                    {isRedemption ? '0 EUR' : `${formatPrice(bundleTotalPrice)} EUR`}
                                </span>
                            </div>
                            {/* Bundle details - show per-session price */}
                            {isBundle && item.bundleUnitPriceGross && item.bundleUnitQuantity && (
                                <div className='ml-6 text-xs text-burgundy/60 font-light'>
                                    ({item.bundleUnitQuantity} × {formatPrice(item.bundleUnitPriceGross)} EUR {language === Language.GERMAN ? 'pro Sitzung' : 'per session'})
                                </div>
                            )}
                            {/* Bundle redemption - single unit price */}
                            {isRedemption && (
                                <div className='ml-6 text-xs text-burgundy/60 font-light'>
                                    ({language === Language.GERMAN ? 'Bundle-Einlösung' : 'Bundle redemption'})
                                </div>
                            )}
                            {/* Add-ons */}
                            {item.addOns.length > 0 && (
                                <div className='flex flex-col gap-1 ml-4'>
                                    {item.addOns.map((addon) => (
                                        <div key={addon.addOnId} className='flex flex-row items-center justify-between'>
                                            <span className='text-xs text-burgundy/70 font-light'>
                                                + {language === Language.GERMAN ? addon.addOnNameDe : addon.addOnNameEn}
                                            </span>
                                            <span className='text-xs text-burgundy/70 font-light'>
                                                {formatPrice(addon.price)} EUR
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {totalDiscount > 0 && (
                    <div className='flex flex-row items-center justify-between'>
                        <span className='text-sm font-light text-green-700'>
                            {language === Language.GERMAN ? 'Rabatt' : 'Discount'}{discountCode ? ` ${discountCode}` : ''}
                        </span>
                        <span className='text-sm font-light text-green-700'>
                            -{formatPrice(totalDiscount)} EUR
                        </span>
                    </div>
                )}
            </div>

            <div className='h-px bg-burgundy w-full' />

            <div className='flex flex-row items-center justify-between'>
                <span className='text-sm font-semibold text-burgundy'>
                    {language === Language.GERMAN ? (cmsConfig?.totalLabelDe ?? 'Gesamt') : (cmsConfig?.totalLabelEn ?? 'Total')}
                </span>
                <span className='text-sm font-semibold text-burgundy'>
                    {formatPrice(displayTotalAfterDiscount)} EUR
                </span>
            </div>

            {children && (
                <div className='mt-2'>
                    {children}
                </div>
            )}
        </div>
    );
};

export default ServicesList;
